import type { Action } from '@0xtorch/core'
import { divideArrayIntoChunks } from '@pkg/basic'
import { and, eq, inArray, ne } from 'drizzle-orm'
import {
  actionAccountRelationColumnCount,
  actionColumnCount,
  actionSourceColumnCount,
  actionTransferColumnCount,
  cryptoCurrencyColumnCount,
  nftColumnCount,
  singleActionRuleColumnCount,
  sqliteMaxCompoundSelectCount,
  sqliteMaxHostParameterCount,
} from '../../constants'
import { actionsToInsertSqls } from '../../createSql/actionsToInsertSqls'
import {
  actionAccountRelationTable,
  actionSourceTable,
  actionTable,
  actionTransferTable,
  cryptoCurrencyTable,
  nftTable,
  portfolioTable,
  singleActionRuleTable,
} from '../../schema'
import type { DatabaseWithTransaction } from '../../types'
import { getMaxInsertRowCount } from '../../utils'

type SaveExchangeActionsParameters = {
  database: DatabaseWithTransaction
  accountId: number
  actions: readonly Action[]
  transferSources: readonly string[]
}

/** exchange API から取得した actions , sources を保存
 * - portfolio の　isTxGenerated を false に変更
 * - action source に紐づく action account relation 削除
 * - action source に紐づく action transfer 削除
 * - action source に紐づく action 削除
 * - action 関連データを保存
 *   - action source (on conflict do nothing)
 *   - action
 *   - action transfer
 *   - crypto currency
 *   - nft
 *   - action account relation
 * - transfer source から作成した single action rule を保存 (on conflict do nothing)
 * - single action rule に該当する action を更新
 */
export const saveExchangeActions = async ({
  database: { transaction },
  accountId,
  actions,
  transferSources,
}: SaveExchangeActionsParameters) => {
  if (actions.length === 0 && transferSources.length === 0) {
    return
  }

  const actionSourceSet = new Set<string>()
  for (const action of actions) {
    actionSourceSet.add(action.source)
  }
  const actionSources = [...actionSourceSet]

  const { actionSqls, actionTransferSqls, cryptoSqls, nftSqls } =
    actionsToInsertSqls(actions)

  const actionAccountRelations: { actionId: string; accountId: number }[] = []
  for (const action of actions) {
    actionAccountRelations.push({
      actionId: `${action.source}_${action.order}`,
      accountId,
    })
  }

  const rules: (typeof singleActionRuleTable.$inferInsert)[] = []
  for (const source of transferSources) {
    // `gift` to `receive-from-cex`
    rules.push({
      source,
      targetType: 'gift',
      newType: 'receive-from-cex',
    })
    // `income` to `receive-from-cex`
    rules.push({
      source,
      targetType: 'income',
      newType: 'receive-from-cex',
    })
    // `fee` to `send-to-cex`
    rules.push({
      source,
      targetType: 'fee',
      newType: 'send-to-cex',
    })
    // `transfer` to `send-to-cex`
    rules.push({
      source,
      targetType: 'transfer',
      newType: 'send-to-cex',
    })
  }

  await transaction([
    (tx) => [
      // portfolio の　isTxGenerated を false に変更
      tx
        .update(portfolioTable)
        .set({ isTxGenerated: false }),
      // action source に紐づく action 削除
      ...divideArrayIntoChunks(actionSources, sqliteMaxHostParameterCount).map(
        (chunk) =>
          tx
            .delete(actionTable)
            .where(inArray(actionTable.sourceId, [...chunk])),
      ),
    ],
    (tx) => [
      // action source を保存 (on conflict do nothing)
      ...divideArrayIntoChunks(
        actionSources,
        getMaxInsertRowCount(actionSourceColumnCount),
      ).map((chunk) =>
        tx
          .insert(actionSourceTable)
          .values(chunk.map((id) => ({ id, type: 'other' as const })))
          .onConflictDoNothing(),
      ),
      // crypto currency を保存 (on conflict do nothing)
      ...divideArrayIntoChunks(
        cryptoSqls,
        getMaxInsertRowCount(cryptoCurrencyColumnCount),
      ).map((chunk) =>
        tx
          .insert(cryptoCurrencyTable)
          .values([...chunk])
          .onConflictDoNothing(),
      ),
      // nft を保存 (on conflict do nothing)
      ...divideArrayIntoChunks(
        nftSqls,
        getMaxInsertRowCount(nftColumnCount),
      ).map((chunk) =>
        tx
          .insert(nftTable)
          .values([...chunk])
          .onConflictDoNothing(),
      ),
      // action を保存
      ...divideArrayIntoChunks(
        actionSqls,
        // SQLITE_MAX_COMPOUND_SELECT を意識して数を調整
        Math.min(
          getMaxInsertRowCount(actionColumnCount + 3),
          Math.floor(sqliteMaxCompoundSelectCount / 3),
        ),
      ).map((chunk) => tx.insert(actionTable).values([...chunk])),
      // action transfer を保存
      ...divideArrayIntoChunks(
        actionTransferSqls,
        getMaxInsertRowCount(actionTransferColumnCount),
      ).map((chunk) => tx.insert(actionTransferTable).values([...chunk])),
      // action account relation を保存 (on conflict do nothing)
      ...divideArrayIntoChunks(
        actionAccountRelations,
        getMaxInsertRowCount(actionAccountRelationColumnCount),
      ).map((chunk) =>
        tx
          .insert(actionAccountRelationTable)
          .values([...chunk])
          .onConflictDoNothing(),
      ),
      // single action rule を保存 (on conflict do nothing)
      ...divideArrayIntoChunks(
        rules,
        getMaxInsertRowCount(singleActionRuleColumnCount),
      ).map((chunk) =>
        tx
          .insert(singleActionRuleTable)
          .values([...chunk])
          .onConflictDoNothing(),
      ),
      // single action rule に該当する action を更新
      ...rules.map((rule) =>
        tx
          .update(actionTable)
          .set({
            type: rule.newType,
            evidence: 'user-rule',
            generatedTx: false,
          })
          .where(
            and(
              eq(actionTable.sourceId, rule.source),
              eq(actionTable.type, rule.targetType),
              ne(actionTable.locked, true),
            ),
          ),
      ),
    ],
  ])
}
